// Extrude handler — MagicaVoxel-style face extrude on top of the voxel
// grid. Press on a voxel face flood-fills the connected coplanar region
// (same palette, face exposed in the same direction) and applies the
// first layer immediately (MagicaVoxel acts on click, before any drag).
// The two tools then diverge in how the rest of the depth is driven:
//
//   ERASE is raycast-ghost (see handleExtrudeDrag + types.ts). Each frame
//   the pick collides with the original object treated as still-whole —
//   erased cells are fed back as voxelRaycastInclude — so the cursor
//   sits on whatever face the laser points at, never axis-locked. The
//   picked cell's depth along the normal sets how many layers peel.
//   Erasing is a preview; the object commits on release.
//
//   DRAW pulls a face outward into empty space, where there's nothing to
//   ray-collide with, so its depth is projection-driven: the laser
//   endpoint is projected onto the normal and the signed drag in cells
//   maps to the target depth (1 cell of drag = 1 layer).
//
// Release commits whatever the final state is — no rollback. Bounds
// checks reject layers outside the work volume; DRAW skips cells already
// occupied by something we didn't place, ERASE skips cells already empty.

import * as THREE from "three";
import { controllerStates } from "../../input/controllers";
import {
  CELL_SIZE,
  type ChunkStorage,
  type PaletteIndex,
  type VoxelGrid,
} from "../../model/voxel-grid";
import { faceNormal } from "../../model/voxel-pick";
import { recordSnapshot, suppressRecording } from "../../model/history";
import { isMirrorActive, mirrorBoxes, mirrorCells } from "../mirror";
import { laserEndpointInGridLocal } from "../laser-endpoint";
import { type CellBounds, type VoxelTarget, cellInBounds } from "../pick";
import { interaction, setInteraction } from "../state";
import type {
  ExtrudeLayerCell,
  ExtrudeSourceCell,
  Interaction,
} from "../types";

type ExtrudeState = Extract<
  Interaction,
  | { kind: "extrude_draw" }
  | { kind: "extrude_erase" }
  | { kind: "extrude_recolor" }
>;

// main injects the whole-grid snapshot capture (it owns the full ModelSnapshot).
// When wired, the extrude drag writes cells with per-cell history SUPPRESSED and
// records ONE snapshot diff at commit — same as box. That removes the per-cell
// recordChange that made a large extrude lag. Without it (tests) the drag falls
// back to per-cell history.
let captureSnapshot: (() => unknown) | null = null;
export function setExtrudeSnapshotCapture(fn: (() => unknown) | null): void {
  captureSnapshot = fn;
}
// Snapshot taken at gesture start; kept until commit records the one undo step.
let extrudeSnapBefore: unknown = null;

// Preview-on-release for the common case: a RECTANGULAR draw face (no selection
// mask) is just a box fill of the extruded slab, so — like box-draw — we write
// NOTHING during the drag (just track depth + show a BoxPreview shell, which
// rebuilds in O(perimeter)) and bulk-fill the slab once on release. That avoids
// re-meshing a whole face cross-section every layer, which is what made a big
// extrude lag. Mask faces, selections, erase, and recolor stay live.
interface SlabAABB { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number; }
type DraftCell = { ix: number; iy: number; iz: number };
let previewSlab:
  | { bbox: SlabAABB; normal: readonly [number, number, number]; palette: PaletteIndex }
  | null = null;

// The extruded slab's inclusive AABB at `depth`: the source face's in-plane bbox,
// swept [1..depth] cells along the outward normal.
function slabAt(p: { bbox: SlabAABB; normal: readonly [number, number, number] }, depth: number): SlabAABB {
  const b = p.bbox;
  const [nx, ny, nz] = p.normal;
  const s = { x0: b.x0, x1: b.x1, y0: b.y0, y1: b.y1, z0: b.z0, z1: b.z1 };
  if (nx > 0) { s.x0 = b.x1 + 1; s.x1 = b.x1 + depth; }
  else if (nx < 0) { s.x1 = b.x0 - 1; s.x0 = b.x0 - depth; }
  else if (ny > 0) { s.y0 = b.y1 + 1; s.y1 = b.y1 + depth; }
  else if (ny < 0) { s.y1 = b.y0 - 1; s.y0 = b.y0 - depth; }
  else if (nz > 0) { s.z0 = b.z1 + 1; s.z1 = b.z1 + depth; }
  else { s.z1 = b.z0 - 1; s.z0 = b.z0 - depth; }
  return s;
}

// The current preview box (for main's BoxPreview shell), or null when not in
// rectangular-draw preview mode (mask/erase/recolor show their live edit).
export function extrudeDraftBox(): { a: DraftCell; b: DraftCell; palette: PaletteIndex } | null {
  if (previewSlab === null || interaction.kind !== "extrude_draw" || interaction.currentDepth < 1) {
    return null;
  }
  const s = slabAt(previewSlab, interaction.currentDepth);
  return {
    a: { ix: s.x0, iy: s.y0, iz: s.z0 },
    b: { ix: s.x1, iy: s.y1, iz: s.z1 },
    palette: previewSlab.palette,
  };
}

// Bulk erase for the common case: a RECTANGULAR erase face (no selection) peeled
// inward is just a box clear of the slab, so — like box-erase — each depth step
// bulk-clears the layer(s) it newly covers (and restores them from a start
// snapshot on a back-drag) instead of deleting cell by cell while accumulating a
// string key + object PER cell. The per-cell path measured ~20x slower on a deep
// erase. Mask faces, selections, recolor stay on the per-cell path.
let eraseSlab: { bbox: SlabAABB; normal: readonly [number, number, number] } | null = null;
// Bounded/incremental undo for the bulk slab paths (draw + erase): only the
// chunks the edit touches, captured first-touch as the slab grows. Replaces the
// two whole-grid clones a deep erase used to take at press (the #5 win), and
// doubles as the back-drag restore buffer for erase. null for the mask path,
// which keeps the whole-grid extrudeSnapBefore.
let undoChunks: Map<number, ChunkStorage | null> | null = null;

// When the raymarcher is the active renderer, a rectangular extrude_draw writes
// the slab LIVE each layer (like erase) instead of deferring to a preview shell,
// so the model + the growing extrusion are one renderer (no shading seam / leftover
// edge). main toggles this with the renderer.
let liveDrawEnabled = false;
export function setExtrudeLiveDraw(on: boolean): void {
  liveDrawEnabled = on;
}

// A bounded undo step the history stores opaque; main's onSnapshotApply restores
// just these chunks (vs a whole ModelSnapshot).
interface SubsetSnapshot { subsetChunks: Map<number, ChunkStorage | null>; }

// The DRAW layers strictly beyond dLo, up to dHi — the grow/shrink delta swept
// OUTWARD from the face (the mirror of eraseDeltaBox).
function drawDeltaBox(p: { bbox: SlabAABB; normal: readonly [number, number, number] }, dLo: number, dHi: number): SlabAABB {
  const b = p.bbox;
  const [nx, ny, nz] = p.normal;
  const s = { x0: b.x0, x1: b.x1, y0: b.y0, y1: b.y1, z0: b.z0, z1: b.z1 };
  if (nx > 0) { s.x0 = b.x1 + dLo + 1; s.x1 = b.x1 + dHi; }
  else if (nx < 0) { s.x1 = b.x0 - dLo - 1; s.x0 = b.x0 - dHi; }
  else if (ny > 0) { s.y0 = b.y1 + dLo + 1; s.y1 = b.y1 + dHi; }
  else if (ny < 0) { s.y1 = b.y0 - dLo - 1; s.y0 = b.y0 - dHi; }
  else if (nz > 0) { s.z0 = b.z1 + dLo + 1; s.z1 = b.z1 + dHi; }
  else { s.z1 = b.z0 - dLo - 1; s.z0 = b.z0 - dHi; }
  return s;
}

// The whole erased slab at `depth` (the source bbox swept [1..depth] cells INWARD
// from the surface) — the pick's includeBox, so the cursor stays on the original
// surface instead of drilling into the pit it's carving.
function eraseFullBox(p: { bbox: SlabAABB; normal: readonly [number, number, number] }, depth: number): SlabAABB {
  const b = p.bbox;
  const [nx, ny, nz] = p.normal;
  const s = { x0: b.x0, x1: b.x1, y0: b.y0, y1: b.y1, z0: b.z0, z1: b.z1 };
  if (nx > 0) { s.x0 = b.x0 - depth + 1; s.x1 = b.x0; }
  else if (nx < 0) { s.x0 = b.x0; s.x1 = b.x0 + depth - 1; }
  else if (ny > 0) { s.y0 = b.y0 - depth + 1; s.y1 = b.y0; }
  else if (ny < 0) { s.y0 = b.y0; s.y1 = b.y0 + depth - 1; }
  else if (nz > 0) { s.z0 = b.z0 - depth + 1; s.z1 = b.z0; }
  else { s.z0 = b.z0; s.z1 = b.z0 + depth - 1; }
  return s;
}

// The layers strictly deeper than dLo, up to dHi (the grow/shrink delta).
function eraseDeltaBox(p: { bbox: SlabAABB; normal: readonly [number, number, number] }, dLo: number, dHi: number): SlabAABB {
  const b = p.bbox;
  const [nx, ny, nz] = p.normal;
  const s = { x0: b.x0, x1: b.x1, y0: b.y0, y1: b.y1, z0: b.z0, z1: b.z1 };
  if (nx > 0) { s.x1 = b.x0 - dLo; s.x0 = b.x0 - dHi + 1; }
  else if (nx < 0) { s.x0 = b.x0 + dLo; s.x1 = b.x0 + dHi - 1; }
  else if (ny > 0) { s.y1 = b.y0 - dLo; s.y0 = b.y0 - dHi + 1; }
  else if (ny < 0) { s.y0 = b.y0 + dLo; s.y1 = b.y0 + dHi - 1; }
  else if (nz > 0) { s.z1 = b.z0 - dLo; s.z0 = b.z0 - dHi + 1; }
  else { s.z0 = b.z0 + dLo; s.z1 = b.z0 + dHi - 1; }
  return s;
}

// The erased slab as the pick's includeBox (or null when not in bulk-erase mode).
export function extrudeEraseIncludeBox(): SlabAABB | null {
  if (eraseSlab === null || interaction.kind !== "extrude_erase" || interaction.currentDepth < 1) {
    return null;
  }
  return eraseFullBox(eraseSlab, interaction.currentDepth);
}

// 4-connected in-plane offsets for each axis-aligned face normal. Used by
// the flood-fill to walk same-plane neighbours.
function inPlaneOffsets(
  normal: [number, number, number],
): Array<[number, number, number]> {
  if (normal[0] !== 0) {
    return [
      [0, 1, 0],
      [0, -1, 0],
      [0, 0, 1],
      [0, 0, -1],
    ];
  }
  if (normal[1] !== 0) {
    return [
      [1, 0, 0],
      [-1, 0, 0],
      [0, 0, 1],
      [0, 0, -1],
    ];
  }
  return [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
  ];
}

// BFS over the picked face: a cell is included iff it shares the seed's
// palette AND has its `+normal` neighbour empty (i.e. that face is
// exposed). Walks only the two in-plane axes — same-plane connectivity.
//
// Selected (protected) cells are excluded from the source set: extrude must
// not originate from selected voxels (DRAW would otherwise grow new layers
// off a selected face). They also act as a BARRIER — the BFS does not cross
// through them — so a selection spanning the region cuts the plane in two:
// extruding a face on one side won't drag the far side along.
function floodFillFaceMask(
  grid: VoxelGrid,
  seed: ExtrudeSourceCell,
  normal: [number, number, number],
  protectedCells: ReadonlySet<string>,
): ExtrudeSourceCell[] {
  const visited = new Set<string>();
  const offsets = inPlaneOffsets(normal);
  const queue: ExtrudeSourceCell[] = [seed];
  const out: ExtrudeSourceCell[] = [];

  while (queue.length > 0) {
    const c = queue.shift()!;
    const key = `${c.ix},${c.iy},${c.iz}`;
    if (visited.has(key)) continue;
    visited.add(key);

    if (grid.get(c.ix, c.iy, c.iz) !== c.palette) continue;
    if (grid.has(c.ix + normal[0], c.iy + normal[1], c.iz + normal[2])) {
      continue;
    }

    // A selected cell is a wall: don't emit it as a source AND don't flood
    // past it, so the selection cuts the extrudable region.
    if (protectedCells.has(key)) continue;

    out.push(c);
    for (const [dx, dy, dz] of offsets) {
      queue.push({
        ix: c.ix + dx,
        iy: c.iy + dy,
        iz: c.iz + dz,
        palette: c.palette,
      });
    }
  }

  return out;
}

// Largest depth where the deepest affected cell of the gesture is
// still inside the canvas. All source cells share the same normal-
// axis coordinate (coplanar face), so any one of them gives the cap.
//
// DRAW layer d places at src + d * normal — deepest reach in the
// normal direction. In bounds: d ≤ distance from src to the far
// wall along +normal.
//
// ERASE layer d erases at src - (d-1) * normal — depth 1 is src
// itself, depth 2 reaches src - normal, etc. In bounds:
// d - 1 ≤ distance from src to the wall along -normal, so the cap
// is one MORE than the equivalent DRAW cap — that's the off-by-one
// that prevented erasing the last layer (e.g. iy=0 when starting
// from the top of a column).
function maxExtrudeDepth(
  src: ExtrudeSourceCell,
  normal: [number, number, number],
  kind: "extrude_draw" | "extrude_erase" | "extrude_recolor",
  bounds: CellBounds,
): number {
  const axisIdx: 0 | 1 | 2 =
    normal[0] !== 0 ? 0 : normal[1] !== 0 ? 1 : 2;
  const normalSign = normal[axisIdx];
  const srcAxis = axisIdx === 0 ? src.ix : axisIdx === 1 ? src.iy : src.iz;
  const minAxis =
    axisIdx === 0 ? bounds.minIx : axisIdx === 1 ? bounds.minIy : bounds.minIz;
  const maxAxis =
    axisIdx === 0 ? bounds.maxIx : axisIdx === 1 ? bounds.maxIy : bounds.maxIz;
  if (kind === "extrude_draw") {
    return normalSign > 0 ? maxAxis - 1 - srcAxis : srcAxis - minAxis;
  }
  // ERASE / RECOLOR — both peel inward from the source face.
  return normalSign > 0 ? srcAxis - minAxis + 1 : maxAxis - srcAxis;
}

const _scratchEndpoint = new THREE.Vector3();

// VR depth input: the laser endpoint at the grab distance, projected onto
// `normal`. (Desktop drives depth from screen-space drag in main.ts instead —
// see updateDesktopExtrude — because the camera-pinned pointer can't sweep
// along an axis that points toward the viewer.)
function endpointProjectionOnNormal(
  controllerIndex: number,
  grabDistance: number,
  gridGroup: THREE.Object3D,
  normal: [number, number, number],
): number {
  laserEndpointInGridLocal(
    controllerIndex,
    grabDistance,
    gridGroup,
    _scratchEndpoint,
  );
  return (
    _scratchEndpoint.x * normal[0] +
    _scratchEndpoint.y * normal[1] +
    _scratchEndpoint.z * normal[2]
  );
}

// Start an extrude on a voxel hit. Assumes the picked cell is currently
// filled (a voxel hit by construction). No-op if the cell is somehow
// empty (e.g. stale pick after a concurrent mutation).
export function startExtrude(
  hit: VoxelTarget,
  controllerIndex: number,
  tool: "draw" | "erase" | "recolor",
  grid: VoxelGrid,
  gridGroup: THREE.Object3D,
  bounds: CellBounds,
  axisDriven: boolean,
  protectedCells: ReadonlySet<string>,
  // Active palette for RECOLOR (the PAINT tool). Unused by draw/erase, which
  // derive their colour from the cells themselves.
  recolorPalette: PaletteIndex = 0,
): void {
  const palette = grid.get(hit.ix, hit.iy, hit.iz);
  if (palette === 0) return;
  const normal = faceNormal(hit.face);
  const seed: ExtrudeSourceCell = {
    ix: hit.ix,
    iy: hit.iy,
    iz: hit.iz,
    palette,
  };
  const sourceCells = floodFillFaceMask(grid, seed, normal, protectedCells);
  // Whole connected face region is selected → nothing to extrude from.
  if (sourceCells.length === 0) return;
  const grabDistance = hit.distance;
  // Anchor the depth axis at the seed cell's center (grid-local).
  const axisAnchor = new THREE.Vector3(
    (hit.ix + 0.5) * CELL_SIZE,
    (hit.iy + 0.5) * CELL_SIZE,
    (hit.iz + 0.5) * CELL_SIZE,
  );
  const startProjection = endpointProjectionOnNormal(
    controllerIndex,
    grabDistance,
    gridGroup,
    normal,
  );
  const depthKind =
    tool === "draw"
      ? "extrude_draw"
      : tool === "recolor"
        ? "extrude_recolor"
        : "extrude_erase";
  const maxDepth = maxExtrudeDepth(seed, normal, depthKind, bounds);

  let state: ExtrudeState;
  if (tool === "draw") {
    state = setInteraction({
      kind: "extrude_draw",
      controllerIndex,
      protectedCells,
      normal,
      sourceCells,
      grabDistance,
      startProjection,
      currentDepth: 0,
      maxDepth,
      perLayer: [],
      axisDriven,
      axisAnchor,
    });
  } else if (tool === "recolor") {
    state = setInteraction({
      kind: "extrude_recolor",
      controllerIndex,
      protectedCells,
      normal,
      sourceCells,
      grabDistance,
      startProjection,
      currentDepth: 0,
      maxDepth,
      perLayer: [],
      axisDriven,
      axisAnchor,
      recolorPalette,
    });
  } else {
    state = setInteraction({
      kind: "extrude_erase",
      controllerIndex,
      protectedCells,
      normal,
      sourceCells,
      grabDistance,
      startProjection,
      currentDepth: 0,
      maxDepth,
      perLayer: [],
      axisDriven,
      axisAnchor,
      editedKeys: new Set<string>(),
    });
  }

  // A full RECTANGULAR face with no selection is a plain box op: draw defers to
  // a preview + fill on release; erase bulk-clears the slab live (with a start
  // snapshot for back-drag restore). Either way it skips the per-cell write that
  // dominates a big extrude. Irregular/masked faces fall through to the live path.
  previewSlab = null;
  eraseSlab = null;
  undoChunks = null;
  if (
    protectedCells.size === 0 &&
    (state.kind === "extrude_draw" || state.kind === "extrude_erase")
  ) {
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (const s of sourceCells) {
      if (s.ix < x0) x0 = s.ix; if (s.ix > x1) x1 = s.ix;
      if (s.iy < y0) y0 = s.iy; if (s.iy > y1) y1 = s.iy;
      if (s.iz < z0) z0 = s.iz; if (s.iz > z1) z1 = s.iz;
    }
    const [nx, ny] = normal;
    const area =
      nx !== 0 ? (y1 - y0 + 1) * (z1 - z0 + 1)
        : ny !== 0 ? (x1 - x0 + 1) * (z1 - z0 + 1)
          : (x1 - x0 + 1) * (y1 - y0 + 1);
    if (sourceCells.length === area) {
      const bbox = { x0, x1, y0, y1, z0, z1 };
      if (state.kind === "extrude_draw") previewSlab = { bbox, normal, palette };
      else eraseSlab = { bbox, normal };
      undoChunks = new Map(); // bounded undo + (erase) back-drag restore buffer
    }
  }

  // Mask/per-cell path: snapshot the whole pre-gesture grid for undo. Bulk slab
  // paths skip this — they accumulate only the touched chunks into undoChunks.
  extrudeSnapBefore =
    undoChunks === null && captureSnapshot ? captureSnapshot() : null;

  // Apply the first layer immediately on press — MagicaVoxel erases/adds
  // one layer on click, before any drag. Further depth comes from the
  // laser-endpoint projection during the drag (handleExtrudeDrag). In preview
  // mode this only advances the depth (the shell), writing nothing.
  if (state.maxDepth >= 1) applyExtrudeDepthChange(state, 1, grid, bounds);
}

// Commit the extrude on release: record the one snapshot undo step (the drag
// wrote no per-cell history when snapshotting is wired). No-op for the per-cell
// fallback path. Call from the gesture commit.
export function commitExtrude(grid: VoxelGrid, bounds: CellBounds): void {
  if (
    interaction.kind !== "extrude_draw" &&
    interaction.kind !== "extrude_erase" &&
    interaction.kind !== "extrude_recolor"
  ) {
    return;
  }
  // Preview mode (mesh renderer): the drag wrote nothing — bulk-fill the final
  // slab now (the BoxPreview shell shielded the build), masked + mirrored, like
  // commitBoxDraw, recording the touched chunks (first-touch) before the fill. A
  // LIVE draw (raymarch) already wrote the slab during the drag, so skip the fill
  // and just record the undo step below.
  if (!liveDrawEnabled && previewSlab !== null && interaction.kind === "extrude_draw" && interaction.currentDepth >= 1) {
    const slab = slabAt(previewSlab, interaction.currentDepth);
    for (const b of mirrorBoxes(slab)) {
      const x0 = Math.max(b.x0, bounds.minIx), x1 = Math.min(b.x1, bounds.maxIx - 1);
      const y0 = Math.max(b.y0, bounds.minIy), y1 = Math.min(b.y1, bounds.maxIy - 1);
      const z0 = Math.max(b.z0, bounds.minIz), z1 = Math.min(b.z1, bounds.maxIz - 1);
      if (x1 < x0 || y1 < y0 || z1 < z0) continue;
      if (undoChunks) grid.captureChunksInto(undoChunks, x0, x1 + 1, y0, y1 + 1, z0, z1 + 1);
      grid.fillBox(x0, x1 + 1, y0, y1 + 1, z0, z1 + 1, previewSlab.palette, true);
    }
  }
  // Record the undo step: a bounded subset for the bulk slab paths (only touched
  // chunks), or the whole-grid snapshot for the mask/per-cell path.
  if (undoChunks !== null) {
    if (captureSnapshot) {
      const before: SubsetSnapshot = { subsetChunks: undoChunks };
      const after: SubsetSnapshot = { subsetChunks: grid.snapshotChunksSubset(undoChunks.keys()) };
      recordSnapshot(before, after);
    }
  } else if (captureSnapshot && extrudeSnapBefore != null) {
    recordSnapshot(extrudeSnapBefore, captureSnapshot());
  }
  extrudeSnapBefore = null;
  previewSlab = null;
  eraseSlab = null;
  undoChunks = null;
}

// Per-frame update during an active extrude. DEPTH is projection-driven
// for both tools: the laser endpoint (controller pos + dir * grabDistance)
// is projected onto the normal axis, and the signed drag in cells sets the
// target depth. This is what lets a sweep ALONG the normal axis keep
// extruding even while the ray still visually hits the occluding front
// face — the endpoint moves deeper though the raycast can't.
//
//   DRAW: positive drag (outward along +normal) grows depth.
//   ERASE: negative drag (inward along -normal) grows depth.
//
// startProjection advances by exactly the drag consumed per layer (1 cell
// of push = 1 layer). On a BLOCKED step (the [0, maxDepth] clamp), the
// anchor is instead reset to the current projection so dragging past a cap
// doesn't accumulate dead distance the user has to drag back through.
//
// The erase CURSOR is handled separately (the standard pick against the
// ghost — see types.ts / main.ts), so the highlight rides the original
// object wherever the laser points, independent of this depth.
export function handleExtrudeDrag(
  grid: VoxelGrid,
  gridGroup: THREE.Object3D,
  bounds: CellBounds,
): void {
  if (
    interaction.kind !== "extrude_draw" &&
    interaction.kind !== "extrude_erase" &&
    interaction.kind !== "extrude_recolor"
  ) {
    return;
  }
  const state = interaction;
  if (!controllerStates[state.controllerIndex].selecting) return;
  // Desktop extrude is driven by screen-space drag in main (updateDesktop
  // extrude); skip the VR endpoint path so the two don't fight.
  if (state.axisDriven) return;

  const currentProjection = endpointProjectionOnNormal(
    state.controllerIndex,
    state.grabDistance,
    gridGroup,
    state.normal,
  );
  const dragGrid = currentProjection - state.startProjection;
  const dragCells = Math.round(dragGrid / CELL_SIZE);
  const desiredChange =
    state.kind === "extrude_draw" ? dragCells : -dragCells;
  if (desiredChange === 0) return;

  const targetDepth = Math.max(
    0,
    Math.min(state.maxDepth, state.currentDepth + desiredChange),
  );

  if (targetDepth !== state.currentDepth) {
    const appliedDepthChange = targetDepth - state.currentDepth;
    applyExtrudeDepthChange(state, targetDepth, grid, bounds);
    const dragSign = state.kind === "extrude_draw" ? 1 : -1;
    state.startProjection += dragSign * appliedDepthChange * CELL_SIZE;
  } else {
    state.startProjection = currentProjection;
  }
}

// Set the extrude to an absolute target depth (clamped to [0, maxDepth]).
// Used by the desktop driver (main), which computes depth from screen drag.
export function applyExtrudeTargetDepth(
  targetDepth: number,
  grid: VoxelGrid,
  bounds: CellBounds,
): void {
  if (
    interaction.kind !== "extrude_draw" &&
    interaction.kind !== "extrude_erase" &&
    interaction.kind !== "extrude_recolor"
  ) {
    return;
  }
  const state = interaction;
  const clamped = Math.max(0, Math.min(state.maxDepth, targetDepth));
  if (clamped === state.currentDepth) return;
  applyExtrudeDepthChange(state, clamped, grid, bounds);
}

function applyExtrudeDepthChange(
  state: ExtrudeState,
  targetDepth: number,
  grid: VoxelGrid,
  bounds: CellBounds,
): void {
  // Rectangular draw. With the mesh renderer: defer — just advance the depth that
  // drives the BoxPreview shell, fill once at commit. With the raymarcher
  // (liveDrawEnabled): write the crossed layers live, mirror-aware, so the
  // extrusion renders as part of the model (no preview shell).
  if (previewSlab !== null) {
    if (liveDrawEnabled && undoChunks !== null) {
      const d0 = state.currentDepth;
      const grow = targetDepth > d0;
      const delta = drawDeltaBox(previewSlab, grow ? d0 : targetDepth, grow ? targetDepth : d0);
      const snap = undoChunks;
      for (const mb of mirrorBoxes(delta)) {
        const x0 = Math.max(mb.x0, bounds.minIx), x1 = Math.min(mb.x1, bounds.maxIx - 1);
        const y0 = Math.max(mb.y0, bounds.minIy), y1 = Math.min(mb.y1, bounds.maxIy - 1);
        const z0 = Math.max(mb.z0, bounds.minIz), z1 = Math.min(mb.z1, bounds.maxIz - 1);
        if (x1 < x0 || y1 < y0 || z1 < z0) continue;
        if (grow) {
          grid.captureChunksInto(snap, x0, x1 + 1, y0, y1 + 1, z0, z1 + 1);
          grid.fillBox(x0, x1 + 1, y0, y1 + 1, z0, z1 + 1, previewSlab.palette, true);
        } else {
          grid.restoreBoxFrom(snap, x0, x1 + 1, y0, y1 + 1, z0, z1 + 1);
        }
      }
    }
    state.currentDepth = targetDepth;
    return;
  }
  // Bulk-erase mode (rectangular erase): clear/restore only the layers the depth
  // change crosses, in bulk + mirror-aware — no per-cell delete/ghost/track.
  if (eraseSlab !== null) {
    const d0 = state.currentDepth;
    const d1 = targetDepth;
    const grow = d1 > d0;
    const delta = eraseDeltaBox(eraseSlab, grow ? d0 : d1, grow ? d1 : d0);
    const snap = undoChunks;
    for (const b of mirrorBoxes(delta)) {
      const x0 = Math.max(b.x0, bounds.minIx), x1 = Math.min(b.x1, bounds.maxIx - 1);
      const y0 = Math.max(b.y0, bounds.minIy), y1 = Math.min(b.y1, bounds.maxIy - 1);
      const z0 = Math.max(b.z0, bounds.minIz), z1 = Math.min(b.z1, bounds.maxIz - 1);
      if (x1 < x0 || y1 < y0 || z1 < z0) continue;
      if (grow) {
        // Record the layer's pre-clear chunks (first-touch) before carving.
        if (snap) grid.captureChunksInto(snap, x0, x1 + 1, y0, y1 + 1, z0, z1 + 1);
        grid.clearBox(x0, x1 + 1, y0, y1 + 1, z0, z1 + 1);
      } else if (snap) {
        grid.restoreBoxFrom(snap, x0, x1 + 1, y0, y1 + 1, z0, z1 + 1);
      }
    }
    state.currentDepth = d1;
    return;
  }
  const run = (): void => {
    while (state.currentDepth < targetDepth) {
      const layerIdx = state.currentDepth + 1;
      state.perLayer.push(addLayer(state, layerIdx, grid, bounds));
      state.currentDepth = layerIdx;
    }
    while (state.currentDepth > targetDepth) {
      const layerIdx = state.currentDepth;
      const cells = state.perLayer.pop() ?? [];
      removeLayer(state, cells, grid);
      state.currentDepth = layerIdx - 1;
    }
  };
  // Suppress per-cell history when snapshotting is wired — the drag's writes are
  // undone in one snapshot (recorded at commit), not thousands of recordChange
  // calls. That per-cell history was the extrude-drag bottleneck.
  if (extrudeSnapBefore != null) suppressRecording(run);
  else run();
}

// Add one layer of the in-progress extrude. Returns the cells actually
// affected (so retract can undo exactly this set).
function addLayer(
  state: ExtrudeState,
  layerIdx: number,
  grid: VoxelGrid,
  bounds: CellBounds,
): ExtrudeLayerCell[] {
  const [nx, ny, nz] = state.normal;
  const out: ExtrudeLayerCell[] = [];
  // Hot-loop fast path: skip mirrorCells' per-cell array+object alloc when no
  // mirror is active (the common case), and skip the protected-set string key
  // unless a selection mask exists. Closures are built once per layer, not per
  // cell. Combined with the suppressed history (applyExtrudeDepthChange), this
  // is what makes a large extrude smooth.
  const mirrorOn = isMirrorActive();
  const hasProtected = state.protectedCells.size > 0;

  if (state.kind === "extrude_draw") {
    // Layer k places a cell at source + k*normal for every source cell. Skip
    // occupied cells (another structure, or an earlier layer of this extrude).
    const place = (ix: number, iy: number, iz: number, palette: PaletteIndex): void => {
      if (!cellInBounds(ix, iy, iz, bounds)) return;
      if (grid.has(ix, iy, iz)) return;
      if (hasProtected && state.protectedCells.has(`${ix},${iy},${iz}`)) return;
      grid.set(ix, iy, iz, palette);
      out.push({ ix, iy, iz, palette });
    };
    for (const src of state.sourceCells) {
      const bx = src.ix + layerIdx * nx;
      const by = src.iy + layerIdx * ny;
      const bz = src.iz + layerIdx * nz;
      if (mirrorOn) for (const c of mirrorCells(bx, by, bz)) place(c.ix, c.iy, c.iz, src.palette);
      else place(bx, by, bz, src.palette);
    }
  } else {
    // ERASE / RECOLOR layer k acts at source - (k-1)*normal. k=1 hits the
    // source cells themselves; deeper layers peel into the object. ERASE
    // deletes (keeping a ghost so the cursor doesn't fall through); RECOLOR
    // sets the active palette in place (geometry intact, no ghost needed).
    const inward = layerIdx - 1;
    const erasing = state.kind === "extrude_erase";
    const editedKeys = state.kind === "extrude_erase" ? state.editedKeys : null;
    const recolorPalette = state.kind === "extrude_recolor" ? state.recolorPalette : 0;
    const act = (ix: number, iy: number, iz: number): void => {
      if (!cellInBounds(ix, iy, iz, bounds)) return;
      if (hasProtected && state.protectedCells.has(`${ix},${iy},${iz}`)) return;
      const existing = grid.get(ix, iy, iz);
      if (existing === 0) return;
      if (erasing) {
        grid.delete(ix, iy, iz);
        // Keep the erased cell collidable for the pick (the ghost) so the
        // cursor still hits the original object until release.
        editedKeys!.add(`${ix},${iy},${iz}`);
      } else {
        grid.set(ix, iy, iz, recolorPalette);
      }
      out.push({ ix, iy, iz, palette: existing });
    };
    for (const src of state.sourceCells) {
      const bx = src.ix - inward * nx;
      const by = src.iy - inward * ny;
      const bz = src.iz - inward * nz;
      if (mirrorOn) for (const c of mirrorCells(bx, by, bz)) act(c.ix, c.iy, c.iz);
      else act(bx, by, bz);
    }
  }

  return out;
}

// Undo one layer's worth of effects. DRAW: delete the cells we placed.
// ERASE: restore cells with their saved palette indices.
function removeLayer(
  state: ExtrudeState,
  cells: ExtrudeLayerCell[],
  grid: VoxelGrid,
): void {
  if (state.kind === "extrude_draw") {
    for (const c of cells) grid.delete(c.ix, c.iy, c.iz);
  } else {
    // ERASE and RECOLOR both restore the cell's saved original palette; only
    // ERASE maintains the collidable-ghost set.
    for (const c of cells) {
      grid.set(c.ix, c.iy, c.iz, c.palette);
      if (state.kind === "extrude_erase") {
        state.editedKeys.delete(`${c.ix},${c.iy},${c.iz}`);
      }
    }
  }
}
